// File: NavBar2.tsx
import * as React from "react";
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Switch,
  Paper,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link, useLocation } from "react-router-dom";
import TourGuide from "../../../../features/components/TourGuide";

// Icons
import PeopleIcon from "@mui/icons-material/People";
import AssignmentIcon from "@mui/icons-material/Assignment";
import SchoolIcon from "@mui/icons-material/School";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import LaunchIcon from "@mui/icons-material/Launch";
import { useSubscription } from "../../../hooks/useSubscription";
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import TextIncreaseIcon from '@mui/icons-material/TextIncrease';
import TextDecreaseIcon from '@mui/icons-material/TextDecrease';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

interface Props {
  isAuthenticated: boolean;
  logout: () => void;
  email?: string;
  toggleTheme: () => void;
  increaseFontSize: () => void;
  decreaseFontSize: () => void;
  resetFontSize: () => void;
  cycleFontSize: () => void;
  fontSizeMultiplier: number;
}

type Item = {
  label: string;
  path: string;
  Icon: typeof PeopleIcon;
  adminOnly?: boolean; // ✅ recommended
  external?: boolean; // For external links
  bookmark?: boolean; // For detached bookmark-style buttons (icon only)
};

const MENU_ITEMS: Item[] = [
  { label: "Patients", path: "/patients", Icon: PeopleIcon, adminOnly: true },
  { label: "Cases", path: "/images", Icon: AssignmentIcon, bookmark: true }, // Bookmark: icon-only, detached from nav

  // 🔒 admin-only
  // { label: "Finalized", path: "/finalizedreports", Icon: CheckCircleIcon,  },

  { label: "About", path: "/account", Icon: InfoIcon, adminOnly: true },
  
  // External link to MoleScan
  { label: "MoleScan", path: "https://mole.noetiv.com", Icon: LaunchIcon, external: true },

  // "FirstSteps" triggers tour, not navigation
  { label: "FirstSteps", path: "/onboarding", Icon: SchoolIcon },
];

export default function NavBar2({
  isAuthenticated,
  email,
  logout,
  toggleTheme,
  increaseFontSize,
  decreaseFontSize,
  resetFontSize,
  cycleFontSize,
  fontSizeMultiplier,
}: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();

  // ✅ pull admin status
  const { isAdmin, loading: subLoading } = useSubscription();

  const [checked, setChecked] = React.useState(theme.palette.mode === "dark");
  const [runTour, setRunTour] = React.useState(false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
    toggleTheme();
  };

  const groupBg = theme.palette.mode === "dark" ? "#191919" : "#f5f5f7";
  const bubbleBg = theme.palette.mode === "dark" ? "#2a2a2e" : "#eeeeef";
  const bubbleHover = theme.palette.mode === "dark" ? "#343438" : "#e2e2e6";

  const isActive = (path: string, label?: string) => {
    // Special handling for "Cases" button - highlight when viewing a case
    if (label === "Cases") {
      return location.pathname.startsWith("/cases/") || 
             location.pathname === "/images" || 
             location.pathname.startsWith("/images/");
    }
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  const tourSteps = [
    {
      target: '[data-tour="patients"]',
      content: "Access and manage your patient records here",
      disableBeacon: true,
    },
    {
      target: '[data-tour="cases"]',
      content: "View all your medical cases and images in one place",
      disableBeacon: true,
    },
    {
      target: '[data-tour="about"]',
      content: "Manage your account settings and preferences",
      disableBeacon: true,
    },
    {
      target: '[data-tour="molescan"]',
      content: "Access the MoleScan external tool for quick skin lesion analysis",
      disableBeacon: true,
    },
    {
      target: '[data-tour="theme-switch"]',
      content: "Toggle between light and dark mode for comfortable viewing",
      disableBeacon: true,
    },
  ];

  const handleStartTour = (e: React.MouseEvent) => {
    e.preventDefault();
    localStorage.removeItem("tour_navbar-tour_completed");
    localStorage.removeItem("tour_navbar-tour_step");
    setRunTour(true);
  };

  const handleFinishTour = () => {
    setRunTour(false);
  };

  // ✅ recommended: filter menu items by policy
  const visibleMenuItems = React.useMemo(() => {
    // If subscription is still loading, hide admin-only items to prevent flicker
    if (subLoading) return MENU_ITEMS.filter((i) => !i.adminOnly);
    return MENU_ITEMS.filter((i) => i.adminOnly || isAdmin);
  }, [subLoading, isAdmin]);

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: theme.palette.mode === "dark" ? "#0f0f10" : "#ffffff",
        color: theme.palette.mode === "dark" ? "#ffffff" : "#000000",
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Toolbar sx={{ 
        justifyContent: "space-between", 
        gap: isMobile ? 0.5 : 2, 
        minHeight: isMobile ? 56 : 72,
        px: isMobile ? 1 : 2
      }}>
        {/* Left: Logo */}
        <Link to={"/"}>
          <img
            src={
              theme.palette.mode === "dark"
                ? "/assets/noetivwhite.png"
                : "/assets/noetivblack.png"
            }
            height={isMobile ? 32 : 44}
            alt="Noetiv"
            loading="lazy"
          />
        </Link>

        {/* Center: Bubble menu */}
        <Box sx={{ 
          flexGrow: 1, 
          display: "flex", 
          justifyContent: "center", 
          alignItems: "center", 
          gap: isMobile ? 0.5 : 2,
          overflow: "auto",
          mx: isMobile ? 0.5 : 0
        }}>
          {/* Bookmark buttons (detached, icon-only) */}
          {visibleMenuItems.filter(item => item.bookmark).map(({ label, path, Icon, external }) => {
            const active = isActive(path, label);
            
            return (
              <Tooltip key={path} title={label} arrow>
                <IconButton
                  component={external ? "a" : Link}
                  href={external ? path : undefined}
                  to={!external ? path : undefined}
                  target={external ? "_blank" : undefined}
                  rel={external ? "noopener noreferrer" : undefined}
                  aria-label={label}
                  data-tour={label.toLowerCase()}
                  sx={{
                    width: isMobile ? 36 : 48,
                    height: isMobile ? 36 : 48,
                    borderRadius: isMobile ? 2 : 3,
                    backgroundColor: active ? theme.palette.primary.main : alpha(groupBg, 0.6),
                    color: active ? "#fff" : theme.palette.text.secondary,
                    backdropFilter: "blur(10px)",
                    border: active 
                      ? `2px solid ${theme.palette.primary.main}`
                      : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    boxShadow: active
                      ? `0 ${isMobile ? 4 : 8}px ${isMobile ? 12 : 24}px ${alpha(theme.palette.primary.main, 0.35)}, 0 0 0 ${isMobile ? 2 : 4}px ${alpha(theme.palette.primary.main, 0.1)}`
                      : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      backgroundColor: active ? theme.palette.primary.dark : alpha(groupBg, 0.9),
                      transform: isMobile ? "scale(1.05)" : "translateY(-2px)",
                      boxShadow: active
                        ? `0 ${isMobile ? 6 : 12}px ${isMobile ? 16 : 32}px ${alpha(theme.palette.primary.main, 0.45)}, 0 0 0 ${isMobile ? 2 : 4}px ${alpha(theme.palette.primary.main, 0.15)}`
                        : `0 8px 16px ${alpha(theme.palette.common.black, 0.15)}`,
                    },
                    "&:active": {
                      transform: "translateY(0)",
                    },
                  }}
                >
                  <Icon sx={{ fontSize: isMobile ? 18 : 22 }} />
                </IconButton>
              </Tooltip>
            );
          })}
          
          {/* Regular nav items */}
          <Paper
            elevation={0}
            sx={{
              px: isMobile ? 0.5 : 1,
              py: 0.5,
              display: "flex",
              alignItems: "center",
              gap: isMobile ? 0.25 : 0.5,
              borderRadius: 999,
              backgroundColor: groupBg,
              border: `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
            }}
          >
            {visibleMenuItems.filter(item => !item.bookmark).map(({ label, path, Icon, external }) => {
              const active = isActive(path, label);
              const isHowTo = label === "FirstSteps";

              const howToBgColor = isHowTo
                ? "#e91e63"
                : active
                ? alpha(theme.palette.primary.main, 0.2)
                : bubbleBg;

              const howToColor = isHowTo
                ? "#ffffff"
                : active
                ? theme.palette.primary.main
                : theme.palette.text.primary;

              const howToHoverBg = isHowTo
                ? "#d81b60"
                : active
                ? alpha(theme.palette.primary.main, 0.28)
                : bubbleHover;

              if (isMobile) {
                return (
                  <Tooltip key={path} title={label} arrow>
                    <IconButton
                      component={external ? "a" : isHowTo ? "button" : Link}
                      href={external ? path : undefined}
                      to={!external && !isHowTo ? path : undefined}
                      target={external ? "_blank" : undefined}
                      rel={external ? "noopener noreferrer" : undefined}
                      aria-label={label}
                      onClick={isHowTo ? handleStartTour : undefined}
                      data-tour={label.toLowerCase()}
                      sx={{
                        borderRadius: 999,
                        width: 32,
                        height: 32,
                        backgroundColor: howToBgColor,
                        color: howToColor,
                        "&:hover": { backgroundColor: howToHoverBg },
                      }}
                    >
                      <Icon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                );
              }

              return (
                <Button
                  key={path}
                  component={external ? "a" : isHowTo ? "button" : Link}
                  href={external ? path : undefined}
                  to={!external && !isHowTo ? path : undefined}
                  target={external ? "_blank" : undefined}
                  rel={external ? "noopener noreferrer" : undefined}
                  startIcon={<Icon sx={{ fontSize: 18 }} />}
                  variant="contained"
                  disableElevation
                  onClick={isHowTo ? handleStartTour : undefined}
                  data-tour={label.toLowerCase()}
                  sx={{
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "0.9rem",
                    px: 2,
                    minHeight: 40,
                    backgroundColor: isHowTo
                      ? "#e91e63"
                      : active
                      ? theme.palette.primary.main
                      : bubbleBg,
                    color: isHowTo
                      ? "#ffffff"
                      : active
                      ? "#fff"
                      : theme.palette.text.primary,
                    boxShadow:
                      active && !isHowTo
                        ? `0 0 0 1px ${alpha(theme.palette.primary.light, 0.6)} inset`
                        : "none",
                    "&:hover": {
                      backgroundColor: isHowTo
                        ? "#d81b60"
                        : active
                        ? theme.palette.primary.dark
                        : bubbleHover,
                    },
                  }}
                >
                  {label}
                </Button>
              );
            })}
          </Paper>
        </Box>

        {/* Right: Theme switch + email + logout */}
        <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? 0.5 : 1.5, flexShrink: 0 }}>
          <Switch
            color="default"
            checked={checked}
            onChange={handleChange}
            inputProps={{ "aria-label": "toggle theme" }}
            data-tour="theme-switch"
            size={isMobile ? "small" : "medium"}
          />
          
          {/* Font Size Controls - show all buttons on both mobile and desktop */}
          <Box sx={{ 
            display: "flex", 
            alignItems: "center", 
            gap: isMobile ? 0.25 : 0.5, 
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            borderRadius: 2,
            p: isMobile ? 0.25 : 0.5
          }}>
            <Tooltip title="Decrease font size" arrow>
              <span>
                <IconButton 
                  size="small" 
                  onClick={decreaseFontSize} 
                  disabled={fontSizeMultiplier <= 0.8}
                  sx={{
                    width: isMobile ? 24 : 32,
                    height: isMobile ? 24 : 32,
                    '&.Mui-disabled': {
                      opacity: 0.3,
                    }
                  }}
                >
                  <TextDecreaseIcon sx={{ fontSize: isMobile ? 14 : 18 }} />
                </IconButton>
              </span>
            </Tooltip>
            
            {!isMobile && (
              <Tooltip title="Reset font size" arrow>
                <IconButton 
                  size="small" 
                  onClick={resetFontSize}
                  sx={{
                    width: 32,
                    height: 32,
                  }}
                >
                  <RestartAltIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            
            {isMobile && (
              <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 600, lineHeight: 1, px: 0.5 }}>
                {Math.round(fontSizeMultiplier * 100)}%
              </Typography>
            )}
            
            <Tooltip title="Increase font size" arrow>
              <span>
                <IconButton 
                  size="small" 
                  onClick={increaseFontSize} 
                  disabled={fontSizeMultiplier >= 1.5}
                  sx={{
                    width: isMobile ? 24 : 32,
                    height: isMobile ? 24 : 32,
                    '&.Mui-disabled': {
                      opacity: 0.3,
                    }
                  }}
                >
                  <TextIncreaseIcon sx={{ fontSize: isMobile ? 14 : 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          
          {!isMobile && email && <Typography variant="body2">{email}</Typography>}
          {isAuthenticated && (
            <Tooltip title={isMobile ? "Logout" : ""} arrow>
              <IconButton
                size={isMobile ? "small" : "medium"}
                onClick={logout}
                sx={{
                  borderRadius: 2,
                  width: isMobile ? 36 : 40,
                  height: isMobile ? 36 : 40,
                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                  color: theme.palette.error.main,
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.error.main, 0.2),
                  },
                }}
              >
                <PowerSettingsNewIcon sx={{ fontSize: isMobile ? 18 : 20 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Toolbar>

      <TourGuide
        steps={tourSteps}
        tourId="navbar-tour"
        run={runTour}
        continuous={true}
        showSkipButton={true}
        showProgress={true}
        onFinish={handleFinishTour}
        onSkip={handleFinishTour}
      />
    </AppBar>
  );
}